import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useDynamicLogo } from "@/lib/useLogo";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function LoginPage({ staff = false }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, register, loginWith2FA } = useAuth();
  const logoUrl = useDynamicLogo();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [twoFA, setTwoFA] = useState(null); // { temp_token }
  const [code, setCode] = useState("");

  function finishLogin(u) {
    if (staff && !(u.role === "admin" || u.role === "staff")) {
      toast.error("Not a staff account");
      return;
    }
    const next = loc.state?.from || (u.role === "admin" || u.role === "staff" ? "/staff" : "/my-bookings");
    nav(next, { replace: true });
  }

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(email, password);
        if (u && u.requires_2fa) {
          setTwoFA({ temp_token: u.temp_token });
        } else {
          finishLogin(u);
        }
      } else {
        const u = await register(name, email, password);
        nav("/my-bookings");
        finishLogin(u);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  }

  async function submit2FA(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await loginWith2FA(twoFA.temp_token, code);
      finishLogin(u);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8">
          <img src={logoUrl} alt="Berdhill Rooms" className="h-14" />
        </Link>
        <div className="bg-white border border-brand-border rounded-sm p-8">
          {twoFA ? (
            <>
              <h1 className="font-display text-3xl text-center">Two-factor code</h1>
              <p className="text-center text-sm text-brand-muted mt-2">
                Open your authenticator app and enter the 6-digit code.
              </p>
              <form onSubmit={submit2FA} className="mt-8 space-y-4">
                <input
                  data-testid="twofa-login-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                  autoFocus
                  className="w-full border border-brand-border rounded-sm px-4 py-3 text-center text-2xl tracking-[0.4em] font-mono"
                />
                <button
                  data-testid="twofa-login-submit"
                  disabled={loading}
                  type="submit"
                  className="w-full bg-brand-primary hover:bg-brand-primaryHover text-white py-3 rounded-sm text-sm font-medium disabled:opacity-50"
                >
                  {loading ? "…" : "Verify & sign in"}
                </button>
                <button
                  type="button"
                  onClick={() => { setTwoFA(null); setCode(""); }}
                  className="block w-full text-center text-xs text-brand-muted hover:text-brand-text"
                >
                  ← Back
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl text-center">
                {staff ? "Staff portal" : mode === "login" ? "Welcome back" : "Create account"}
              </h1>
              <p className="text-center text-sm text-brand-muted mt-2">
                {staff ? "Sign in to manage Berdhill Rooms" : mode === "login" ? "Sign in to manage your bookings" : "Save your details for faster booking"}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4">
                {mode === "register" && !staff && (
                  <input
                    data-testid="auth-name"
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full border border-brand-border rounded-sm px-4 py-3 text-sm"
                  />
                )}
                <input
                  data-testid="auth-email"
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full border border-brand-border rounded-sm px-4 py-3 text-sm"
                />
                <input
                  data-testid="auth-password"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full border border-brand-border rounded-sm px-4 py-3 text-sm"
                />
                <button
                  data-testid="auth-submit"
                  disabled={loading}
                  type="submit"
                  className="w-full bg-brand-primary hover:bg-brand-primaryHover text-white py-3 rounded-sm text-sm font-medium disabled:opacity-50"
                >
                  {loading ? "…" : mode === "login" ? "Sign in" : "Create account"}
                </button>
              </form>

              {!staff && (
                <button
                  data-testid="auth-toggle-mode"
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="block w-full text-center mt-6 text-sm text-brand-muted hover:text-brand-text"
                >
                  {mode === "login" ? "New here? Create an account" : "Have an account? Sign in"}
                </button>
              )}
              {!staff && (
                <Link to="/manage" data-testid="manage-by-ref-link" className="block text-center mt-3 text-xs text-brand-primary hover:underline">
                  Manage with booking reference instead →
                </Link>
              )}
              <Link to="/" className="block text-center mt-3 text-xs text-brand-muted hover:text-brand-text">← Back to site</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
